import axios from "axios";
import type {LoginRequest} from "@/contexts/authContext.tsx";
import { storageService, STORAGE_KEYS } from "@/lib/storage";

const BASE_URL = import.meta.env.VITE_API_URL;

/** * 1. PUBLIC CLIENT
 * Used for Login, Sign up, and anything that doesn't require Auth.
 */
export const publicClient = axios.create({
    baseURL: BASE_URL,
    headers: { "Content-Type": "application/json" },
});

/** * 2. PRIVATE CLIENT
 * Used for Enquiries and protected data.
 * Includes an interceptor to inject the token.
 */
export const privateClient = axios.create({
    baseURL: BASE_URL,
    headers: { "Content-Type": "application/json" },
});

// Attach the token and tenant ID to every request made via privateClient
privateClient.interceptors.request.use((config) => {
    const token = storageService.getString(STORAGE_KEYS.authToken);
    if (token) {
        config.headers['huh-ha-token'] = `${token}`;
    }
    config.headers['X-Tenant-ID'] = import.meta.env.VITE_TENANT_ID as string;
    return config;
});


export const authApi = {
    login: (credentials: LoginRequest) => publicClient.post("/login", credentials),
};


export const enquiriesApi = {
    getAll: () =>
      // getAllEnquiries,
        privateClient.get("/enquiries"),

    getOne: (id: string) => privateClient.get(`/enquiries/${id}`),

    deleteOne: (id: string) => privateClient.delete(`/enquiries/${id}`),
};


const connectHeaders = {
    "Connect-Protocol-Version": "1",
    "X-Tenant-ID": import.meta.env.VITE_TENANT_ID as string,
};

export interface InitiateBookingResponse {
    bookingId: string;
    razorpayKey: string;
    razorpayOrderId: string;
    amount: number;       // in paise
    currency: string;
    totalPrice: number;   // in rupees
    guestName: string;
    guestEmail: string;
    guestMobile: string;
}

export interface RoomOccupancyRoom {
    roomId:      string;
    bookedDates: string[]; // individual stay-night dates, e.g. "2025-06-01"
}

export const availabilityApi = {
    getOccupancy: (roomIds: string[]) =>
        publicClient.post<{ rooms: RoomOccupancyRoom[] }>(
            "/resort.v1.RoomService/GetRoomOccupancy",
            { roomIds },
            { headers: connectHeaders }
        ),
};

export const contactApi = {
    submit: (data: { name: string; phone: string; email?: string; message: string }) =>
        publicClient.post<{ id: string; createdAt: string }>(
            "/resort.v1.ContactService/SubmitContact",
            data,
            { headers: connectHeaders }
        ),
};

export const paymentApi = {
    initiateBooking: (data: {
        roomIds:        string[];
        checkIn:        string;
        checkOut:       string;
        guestCount:     number;
        guestTitle:     string;
        guestFirstName: string;
        guestLastName:  string;
        guestEmail:     string;
        guestPhone:     string;
        guestAddress:   string;
        guestState:     string;
        guestCountry:   string;
        guestPincode:   string;
        childrenCount:  number;
        petsCount:      number;
        includeFood:    boolean;
        notes?:         string;
    }) =>
        privateClient.post<InitiateBookingResponse>(
            "/resort.v1.PaymentService/InitiateBooking",
            {
                roomIds:        data.roomIds,
                checkIn:        data.checkIn,
                checkOut:       data.checkOut,
                guestCount:     data.guestCount,
                guestTitle:     data.guestTitle,
                guestFirstName: data.guestFirstName,
                guestLastName:  data.guestLastName,
                guestEmail:     data.guestEmail,
                guestPhone:     data.guestPhone,
                guestAddress:   data.guestAddress,
                guestState:     data.guestState,
                guestCountry:   data.guestCountry,
                guestPincode:   data.guestPincode,
                childrenCount:  data.childrenCount,
                petsCount:      data.petsCount,
                includeFood:    data.includeFood,
                notes:          data.notes ?? "",
            },
            { headers: connectHeaders }
        ),

    verifyPayment: (data: {
        bookingId:         string;
        razorpayOrderId:   string;
        razorpayPaymentId: string;
        razorpaySignature: string;
    }) =>
        privateClient.post(
            "/resort.v1.PaymentService/VerifyPayment",
            {
                bookingId:         data.bookingId,
                razorpayOrderId:   data.razorpayOrderId,
                razorpayPaymentId: data.razorpayPaymentId,
                razorpaySignature: data.razorpaySignature,
            },
            { headers: connectHeaders }
        ),
};